// src/components/ao/FichePoste.jsx
import React, { useRef } from 'react';
import { X, Download, MapPin, Briefcase, Clock, Monitor, Calendar, Award } from 'lucide-react';

// ── Couleurs Wikeys ──────────────────────────────────────────
const BLUE   = '#FF4100';
const ORANGE = '#E8421A';
const DARK   = '#0F1A3E';
const BLUE_LIGHT = '#E8ECFB';
const GRAY_LIGHT = '#F3F4F8';
const GRAY   = 'var(--text-muted)';
const BORDER = '#D1D5EE';
const GREEN  = '#059669';

// ── Composants internes ──────────────────────────────────────
const SectionBadge = ({ title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 10 }}>
    <div style={{
      background: BLUE, color: 'white', fontWeight: 700, fontSize: '0.6875rem',
      letterSpacing: '0.08em', padding: '5px 14px', borderRadius: '4px 0 0 4px',
      whiteSpace: 'nowrap',
    }}>{title}</div>
    <div style={{ flex: 1, height: 2, background: BORDER }} />
  </div>
);

const Chip = ({ text, color = BLUE }) => (
  <span style={{
    display: 'inline-block', padding: '3px 10px', borderRadius: 4,
    background: BLUE_LIGHT, color, fontSize: '0.75rem', fontWeight: 600,
    marginRight: 5, marginBottom: 5, border: `1px solid ${color}33`,
  }}>{text}</span>
);

const MetaItem = ({ icon, label, value }) => value ? (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: GRAY,
      textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
    <div style={{ fontSize: '0.875rem', color: DARK, fontWeight: 500 }}>{value}</div>
  </div>
) : null;

export default function FichePoste({ ao, user, onClose }) {
  const ref = useRef();

  const handleDownload = () => {
    const content = ref.current;
    if (!content) return;

    // Clone + corrections pour impression
    const clone = content.cloneNode(true);

    // 1. Convertir les <li display:flex> en block
    //    Chrome écrase les espaces des text nodes dans un flex container en print
    clone.querySelectorAll('li').forEach(el => {
      el.style.display         = 'block';
      el.style.breakInside     = 'avoid';
      el.style.pageBreakInside = 'avoid';
      el.style.marginBottom    = el.style.marginBottom || '4px';
      // Le span bullet devient inline → ajouter margin-right
      const bullet = el.querySelector('span:first-child');
      if (bullet) bullet.style.marginRight = '6px';
    });

    // 2. Chips (inline-block) : augmenter l'espace entre eux
    clone.querySelectorAll('span').forEach(el => {
      if (el.style.display === 'inline-block') {
        el.style.marginRight = '8px';
        el.style.marginBottom = '6px';
      }
    });

    // 3. Sections (div avec marginBottom) → ne pas couper
    clone.querySelectorAll('div').forEach(el => {
      if (el.style.marginBottom) {
        el.style.breakInside     = 'avoid';
        el.style.pageBreakInside = 'avoid';
      }
    });

    // 4. Paragraphes longs → orphelines/veuves
    clone.querySelectorAll('p').forEach(el => {
      el.style.orphans         = '4';
      el.style.widows          = '4';
      el.style.breakInside     = 'avoid';
      el.style.pageBreakInside = 'avoid';
    });

    const printWindow = window.open('', '_blank', 'width=900,height=1200');
    if (!printWindow) {
      alert('Veuillez autoriser les pop-ups pour télécharger le PDF.');
      return;
    }

    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${(ao.job_title || 'Fiche_de_poste').replace(/\s+/g, '_')}</title>
  <style>
    @page { size: A4 portrait; margin: 10mm 12mm; }
    * {
      box-sizing: border-box;
      font-family: Arial, Helvetica, sans-serif !important;
      -webkit-user-select: text !important;
      user-select: text !important;
    }
    body {
      margin: 0; padding: 0;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      color-adjust: exact;
    }
    img { max-width: 100%; }
  </style>
</head>
<body>${clone.outerHTML}</body>
</html>`);

    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const remu = ao.remuneration_avantages || {};
  const tjm  = remu.tjm || ao.tjm || '';
  const userName = user ? `${user.prenom || ''} ${user.nom || ''}`.trim() || user.email : 'Wikeys';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.6)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 12, width: '100%', maxWidth: 800,
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
      }}>
        {/* ── Toolbar ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '13px 20px', borderBottom: '1px solid #e5e7eb', flexShrink: 0,
        }}>
          <span style={{ fontWeight: 700, fontSize: '0.9375rem', color: DARK }}>
            Aperçu — Fiche de poste
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleDownload} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 16px', borderRadius: 8, border: 'none',
              background: ORANGE, color: 'white', cursor: 'pointer',
              fontWeight: 600, fontSize: '0.8125rem',
            }}>
              <Download size={14}/> Télécharger PDF
            </button>
            <button onClick={onClose} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 34, borderRadius: 8, border: '1px solid #e5e7eb',
              background: 'transparent', cursor: 'pointer',
            }}>
              <X size={16}/>
            </button>
          </div>
        </div>

        {/* ── Preview scrollable ── */}
        <div style={{ overflow: 'auto', flex: 1, padding: 24, background: 'var(--bg-elevated)' }}>
          <div ref={ref} style={{
            background: 'var(--bg-card)', maxWidth: 720, margin: '0 auto',
            fontFamily: "'Segoe UI', Arial, sans-serif", color: DARK,
            boxShadow: '0 4px 24px rgba(26,44,204,0.10)',
          }}>

            {/* ── HEADER SOMBRE ── */}
            <div style={{ background: DARK, padding: '22px 32px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                {/* Logo Wikeys texte stylisé */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  <span style={{ fontWeight: 900, fontSize: '1.625rem', color: BLUE,
                    letterSpacing: '-1px', fontFamily: 'Arial Black, Arial, sans-serif' }}>wi</span>
                  <span style={{ fontWeight: 900, fontSize: '1.625rem', color: ORANGE,
                    letterSpacing: '-1px', fontFamily: 'Arial Black, Arial, sans-serif' }}>k</span>
                  <span style={{ fontWeight: 900, fontSize: '1.625rem', color: BLUE,
                    letterSpacing: '-1px', fontFamily: 'Arial Black, Arial, sans-serif' }}>eys</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.6875rem', color: '#8899DD',
                    fontWeight: 600, letterSpacing: '0.06em' }}>
                    RÉF. {`WIB-${new Date().getFullYear()}-${Math.floor(Math.random()*900+100)}`}
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: '#8899DD', marginTop: 2 }}>
                    {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </div>
                  <div style={{
                    marginTop: 6, display: 'inline-block',
                    background: ORANGE, color: 'white', borderRadius: 4,
                    padding: '2px 10px', fontSize: '0.6875rem', fontWeight: 700,
                    letterSpacing: '0.05em',
                  }}>CONFIDENTIEL</div>
                </div>
              </div>

              {/* Titre + client */}
              <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800,
                color: 'white', lineHeight: 1.3 }}>
                {ao.job_title || 'Poste à pourvoir'}
              </h1>
  
            </div>

            {/* Barre orange */}
            <div style={{ height: 4, background: ORANGE }} />

            {/* ── MÉTADONNÉES ── */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 0, background: GRAY_LIGHT, borderBottom: `1px solid ${BORDER}`,
            }}>
              {[
                { label: 'Localisation',    value: ao.location },
                { label: 'Mode de travail', value: ao.work_mode },
                { label: 'Contrat',         value: ao.contract_type },
                { label: 'TJM indicatif',   value: tjm },
                { label: 'Expérience',      value: ao.experience_years },
                { label: 'Démarrage',       value: ao.start_date || 'À définir' },
              ].filter(m => m.value).map((m, i) => (
                <div key={i} style={{
                  padding: '10px 16px',
                  borderRight: i % 3 !== 2 ? `1px solid ${BORDER}` : 'none',
                  borderBottom: i < 3 ? `1px solid ${BORDER}` : 'none',
                }}>
                  <div style={{ fontSize: '0.625rem', fontWeight: 700, color: GRAY,
                    textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>
                    {m.label}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: DARK, fontWeight: 600 }}>
                    {m.value}
                  </div>
                </div>
              ))}
            </div>

            {/* ── CONTENU ── */}
            <div style={{ padding: '24px 32px 32px' }}>

              {/* Résumé */}
              {ao.executive_summary && (
                <div style={{ marginBottom: 22 }}>
                  <SectionBadge title="RÉSUMÉ DU POSTE" />
                  <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.8,
                    color: 'var(--text-primary)', textAlign: 'justify' }}>
                    {ao.executive_summary}
                  </p>
                </div>
              )}

              {/* Description */}
              {ao.detailed_description && (
                <div style={{ marginBottom: 22 }}>
                  <SectionBadge title="DESCRIPTION DU POSTE" />
                  <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.8,
                    color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                    {ao.detailed_description}
                  </p>
                </div>
              )}

              {/* Objectifs */}
              {ao.objectifs_principaux?.length > 0 && (
                <div style={{ marginBottom: 22 }}>
                  <SectionBadge title="OBJECTIFS PRINCIPAUX" />
                  <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                    {ao.objectifs_principaux.map((o, i) => (
                      <li key={i} style={{ display: 'flex', gap: 8, marginBottom: 5,
                        fontSize: '0.875rem', lineHeight: 1.7, color: 'var(--text-primary)' }}>
                        <span style={{ color: ORANGE, fontWeight: 700, flexShrink: 0 }}>▸</span>
                        {o}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Responsabilités */}
              {ao.responsibilities?.length > 0 && (
                <div style={{ marginBottom: 22 }}>
                  <SectionBadge title="RESPONSABILITÉS" />
                  <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                    {ao.responsibilities.map((r, i) => (
                      <li key={i} style={{ display: 'flex', gap: 8, marginBottom: 5,
                        fontSize: '0.875rem', lineHeight: 1.7, color: 'var(--text-primary)' }}>
                        <span style={{ color: ORANGE, fontWeight: 700, flexShrink: 0 }}>▸</span>
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Compétences techniques */}
              {ao.tech_skills?.length > 0 && (
                <div style={{ marginBottom: 22 }}>
                  <SectionBadge title="COMPÉTENCES TECHNIQUES" />
                  <div>{ao.tech_skills.map(s => <Chip key={s} text={s} color={BLUE} />)}</div>
                </div>
              )}

              {/* Compétences soft */}
              {ao.soft_skills?.length > 0 && (
                <div style={{ marginBottom: 22 }}>
                  <SectionBadge title="COMPÉTENCES COMPORTEMENTALES" />
                  <div>{ao.soft_skills.map(s => <Chip key={s} text={s} color={GREEN} />)}</div>
                </div>
              )}

              {/* Certifications */}
              {ao.certifications?.length > 0 && (
                <div style={{ marginBottom: 22 }}>
                  <SectionBadge title="CERTIFICATIONS SOUHAITÉES" />
                  <div>{ao.certifications.map(s => <Chip key={s} text={s} color={ORANGE} />)}</div>
                </div>
              )}

              {/* Critères */}
              {ao.selection_criteria?.length > 0 && (
                <div style={{ marginBottom: 22 }}>
                  <SectionBadge title="CRITÈRES DE SÉLECTION" />
                  <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                    {ao.selection_criteria.map((c, i) => (
                      <li key={i} style={{ display: 'flex', gap: 8, marginBottom: 5,
                        fontSize: '0.875rem', lineHeight: 1.7, color: 'var(--text-primary)' }}>
                        <span style={{ color: BLUE, fontWeight: 700, flexShrink: 0 }}>◆</span>
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ── FOOTER ── */}
              <div style={{
                marginTop: 28, paddingTop: 18,
                borderTop: `3px solid ${ORANGE}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: GRAY_LIGHT, margin: '28px -32px -32px',
                padding: '16px 32px',
              }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.875rem', color: DARK }}>
                    {userName}
                  </div>
                  {user?.email && (
                    <div style={{ fontSize: '0.75rem', color: GRAY, marginTop: 2 }}>
                      {user.email}
                    </div>
                  )}
                  {user?.telephone && (
                    <div style={{ fontSize: '0.75rem', color: GRAY }}>{user.telephone}</div>
                  )}
                </div>
                {/* Logo Wikeys footer */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 0, justifyContent: 'flex-end' }}>
                    <span style={{ fontWeight: 900, fontSize: '1.25rem', color: BLUE,
                      fontFamily: 'Arial Black, Arial, sans-serif' }}>wi</span>
                    <span style={{ fontWeight: 900, fontSize: '1.25rem', color: ORANGE,
                      fontFamily: 'Arial Black, Arial, sans-serif' }}>k</span>
                    <span style={{ fontWeight: 900, fontSize: '1.25rem', color: BLUE,
                      fontFamily: 'Arial Black, Arial, sans-serif' }}>eys</span>
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: GRAY, marginTop: 2 }}>
                    Document confidentiel © {new Date().getFullYear()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}